import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, ButtonBase, Card, Stack, Typography } from '@mui/material';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import OndemandVideoOutlinedIcon from '@mui/icons-material/OndemandVideoOutlined';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import { GUIDE_VIDEOS, toEmbedUrl } from '../guide/videos';
import { tokens } from '../theme';

// Embedded player in 16:9 that keeps its proportions on any screen width.
const Player = ({ video }) => {
  const src = toEmbedUrl(video.url);
  return (
    <Card sx={{ overflow: 'hidden' }}>
      <Box sx={{ position: 'relative', pt: '56.25%', bgcolor: tokens.ink }}>
        {src ? (
          <Box component="iframe" key={src} src={src} title={video.title}
            allow="clipboard-write; autoplay; encrypted-media; fullscreen; picture-in-picture; screen-wake-lock"
            allowFullScreen
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }} />
        ) : (
          <Typography sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', p: 2, textAlign: 'center' }}>
            Видео временно недоступно
          </Typography>
        )}
      </Box>
      <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Typography variant="h3" component="h2" sx={{ fontSize: '1.125rem', fontWeight: 700 }}>{video.title}</Typography>
        {video.description && <Typography color="text.secondary" sx={{ mt: 0.75 }}>{video.description}</Typography>}
      </Box>
    </Card>
  );
};

const VideoItem = ({ video, index, active, onSelect }) => (
  <ButtonBase onClick={onSelect} aria-current={active ? 'true' : undefined}
    sx={{
      width: '100%', justifyContent: 'flex-start', textAlign: 'left', gap: 1.5, p: 1.5, borderRadius: `${tokens.radiusSm}px`,
      bgcolor: active ? tokens.primarySoft : 'transparent', '&:hover': { bgcolor: active ? tokens.primarySoft : tokens.primaryTint },
    }}>
    <PlayCircleOutlineRoundedIcon sx={{ color: active ? tokens.primaryDark : tokens.inkMuted, flexShrink: 0 }} />
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: tokens.inkMuted }}>Шаг {index + 1}</Typography>
      <Typography sx={{ fontWeight: active ? 700 : 500, color: active ? tokens.primaryDark : tokens.ink, lineHeight: 1.35 }}>{video.title}</Typography>
    </Box>
  </ButtonBase>
);

// "Как пользоваться": video instructions for the service. The list lives in guide/videos.js.
const GuidePage = () => {
  const [params, setParams] = useSearchParams();
  const current = GUIDE_VIDEOS.find((v) => v.id === params.get('v')) || GUIDE_VIDEOS[0];

  useEffect(() => { document.title = 'Как пользоваться — MARQIT'; }, []);

  const select = (id) => {
    setParams({ v: id }, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box>
      <PageHeader overline="Помощь" title="Как пользоваться MARQIT"
        subtitle="Короткие видео: как подключить кабинет, загрузить данные и читать аналитику." />
      {!current ? (
        <Card>
          <EmptyState icon={<OndemandVideoOutlinedIcon fontSize="inherit" />} title="Видеоинструкции скоро появятся"
            description="Мы готовим короткие видео по каждому разделу сервиса." />
        </Card>
      ) : (
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2.5} alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <Player video={current} />
          </Box>
          {GUIDE_VIDEOS.length > 1 && (
            <Card component="nav" aria-label="Список видео" sx={{ width: { xs: '100%', lg: 340 }, flexShrink: 0, p: 1 }}>
              {GUIDE_VIDEOS.map((v, i) => (
                <VideoItem key={v.id} video={v} index={i} active={v.id === current.id} onSelect={() => select(v.id)} />
              ))}
            </Card>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default GuidePage;
